// @ts-strict
import { DocumentType, modelOptions, mongoose, plugin, post, pre, prop } from "@typegoose/typegoose";
import mongooseAutoPopulate from "mongoose-autopopulate";
import { DefaultLoraNetworkProfilePerDevice } from "../../defaultLoraNetworkProfilePerDevice/defaultLoraNetworkProfilePerDevice.js";
import { LoraNetworkProfile } from "../../loraNetworkProfile/loraNetworkProfile.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import { Device } from "../device.js";

const SCHEMA_KEY = "LoraNetworkProfileConfig" as const;

type FieldsWithDefaults = never;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<LoraNetworkProfileConfig>>(...addSchemaKeyHook(SCHEMA_KEY))
@pre<LoraNetworkProfileConfig>(
    "validate",
    async function saveLoraNetworkProfileConfig(this: DocumentType<LoraNetworkProfileConfig>) {
        this._parent_node_id = (this.$parent() as unknown as DocumentType<Device>).node_id;
    },
)
@plugin(mongooseAutoPopulate as any)
export class LoraNetworkProfileConfig extends SchemaBase<LoraNetworkProfileConfig, FieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    readonly schemaKey = SCHEMA_KEY;

    /**
     * this is the parent node_id. This is automatically save to the object via a hook, and should not be
     * explicitly set except in that hook. It is necessary in the DB, but not necessary once loaded from the db,
     * so it is marked optional in TS, but required in Typegoose.
     */
    @prop({ type: () => String, required: true })
    public _parent_node_id?: string;

    @prop({
        ref: () => DefaultLoraNetworkProfilePerDevice,
        required: false,
        foreignField: "node_id",
        localField: "_parent_node_id",
        justOne: true,
        virtual: true,
        autopopulate: true,
    })
    public linkedDefaultLoraNetworkProfile?: DefaultLoraNetworkProfilePerDevice;

    /**
     * this is the id of the network settings profile that was selected manually by an admin and will take
     * priority over the _idOfDefaultLoraNetworkProfile
     */
    @prop({ ref: () => LoraNetworkProfile, required: false })
    public idOfSelectedLoraNetworkProfile?: mongoose.Types.ObjectId;

    @prop({
        type: () => LoraNetworkProfile,
        required: false,
        virtual: true,
        foreignField: "_id",
        localField: "idOfSelectedLoraNetworkProfile",
        ref: "LoraNetworkProfile",
        justOne: true,
        autopopulate: true,
    })
    public selectedLoraNetworkProfile?: LoraNetworkProfile;

    /**
     * this is the manual network settings that override both the idOfSelectedLoraNetworkProfile and
     * _idOfDefaultLoraNetworkProfile for when a device needs one-off truly custom Lora network settings.
     */
    @prop({ type: () => LoraNetworkProfile, required: false })
    public customLoraNetworkSettings?: LoraNetworkProfile;

    static getters = {
        /**
         * Gets the effective LoraNetworkSettings by priority: override > explicit > default
         */
        getEffectiveSettings: (self: LoraNetworkProfileConfig): LoraNetworkProfile | undefined =>
            self.customLoraNetworkSettings ??
            self.selectedLoraNetworkProfile ??
            self.linkedDefaultLoraNetworkProfile?.defaultLoraNetworkProfile,
    } as const satisfies Record<string, (self: LoraNetworkProfileConfig) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    constructor(properties?: SchemaConstructorArgs<LoraNetworkProfileConfig, FieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}
